import './login.scss'
import { useState } from 'react'
import { Paper, TextField, Button, Box, InputLabel, CssBaseline } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { performSetServices, saveClientKey } from '../../features/auth/loginSlice'
import { useTranslation } from 'react-i18next'
import { IOSSwitch } from '../../components/utilities/Switches'
import { updateTheme } from '../../features/theme/themeSlice'
import { useLoginMutation, useVerifyMFAMutation, useActivateMFAMutation } from './../../services/loginApi'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import jwt_decode from 'jwt-decode'
import GuenoLogo from '../../assets/logos/logo-gueno-light2.png'
import GuenoLogoDark from '../../assets/logos/logo-gueno2.png'
import ReactGA from 'react-ga'
import toaster from '../../toaster'
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined'
import TwoFactorActivationModal from './2FA/2FAActivationModal'
import ProcessingSpinner from '../../components/common/alertFetchSpinner/processingSpinner'
import OTPModal from './2FA/OTPModal'
import { getClientKeyWithAccessToken, getClientKeyWithUserPassword } from '../../lib/security'

/**
 * Login component that handles user authentication, including email/password,
 * and Multi-Factor Authentication (MFA).
 * @param {object} props - The component props.
 * @param {function} props.handleUserLogin - Callback function to execute on successful login.
 */
const Login = ({ handleUserLogin }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['common', 'messages'])
  const { theme } = useSelector((state) => state.theme)
  const [userEmail, setUserEmail] = useState('')
  const [userPassword, setUserPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isUserEmailOK, setIsUserEmailOK] = useState(false)
  const [isUserPasswordOK, setIsUserPasswordOK] = useState(false)
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState(0)
  const [secret, setSecret] = useState('')
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false)
  const [verifySuccess, setVerifySuccess] = useState(false)
  let isSignUpBtnOK = isUserEmailOK && isUserPasswordOK

  /**
   * Validates an email string using a regular expression.
   * @param {string} email - The email to validate.
   * @returns {boolean} - True if the email is valid, false otherwise.
   */
  const validateUserEmail = (email) => /[\w+.-]+@[\w-]+\.[\w]{2,4}/.test(email)
  /**
   * Validates that the password is not empty.
   * @param {string} password - The password to validate.
   * @returns {boolean} - True if the password is not empty, false otherwise.
   */
  const validateUserPassword = (password) => password.length > 0

  const [login] = useLoginMutation()
  const [verifyMFA] = useVerifyMFAMutation()
  const [activateMFA] = useActivateMFAMutation()

  /**
   * Displays a toaster notification based on the error message received from the API.
   * @param {string} message - The error message string.
   */
  const handleErrorMessage = (message) => {
    if (message) {
      if (message.includes('INACTIVE_FAILED_ATTEMPTS')) {
        toaster.info(t('messages:attemptsReached'))
      } else if (message.includes('EXPIRED_PASSWORD')) {
        toaster.error(t('messages:passwordExpired'))
      } else if (message.includes('INACTIVE_BY_NOLOGIN')) {
        toaster.error(t('messages:userBlocked'))
      } else {
        toaster.error(message)
      }
    }
  }

  /**
   * Triggers the sign-in process when the 'Enter' key is pressed in an input field.
   * @param {React.KeyboardEvent} evt - The keyboard event.
   */
  const handleKeyPress = (evt) => {
    if (evt.key === 'Enter' && isSignUpBtnOK) {
      handleSignInBtn()
    }
  }

  /**
   * Prevents the default action on mouse down for the password visibility toggle.
   * @param {React.MouseEvent} event - The mouse event.
   */
  const handleMouseDownPassword = (event) => {
    event.preventDefault()
  }

  /**
   * Handles changes to the user email input field, updating state and validation.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event.
   */
  const handleInputUserEmail = (e) => {
    const email = e.target.value
    setUserEmail(email)
    setIsUserEmailOK(validateUserEmail(email))
  }

  /**
   * Handles changes to the user password input field, updating state and validation.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event.
   */
  const handleInputUserPassword = (e) => {
    const password = e.target.value
    setUserPassword(password)
    setIsUserPasswordOK(validateUserPassword(password))
  }

  /**
   * Handles the user sign-in process. It calls the login API and manages
   * the different responses, such as successful login, MFA enrollment, or OTP requirement.
   */
  const handleSignInBtn = async () => {
    setIsLoading(true)
    try {
      const clientKey = await getClientKeyWithUserPassword(userEmail, userPassword)

      const loginResponse = await login({
        userEmail,
        userPassword,
        otp,
        clientKey,
      }).unwrap()

      if (loginResponse.access_token) {
        await setUserServices(loginResponse.access_token)
      }

      if (loginResponse.mfaEnrollInfo) {
        setQrCode(loginResponse.mfaEnrollInfo?.barcode)
        setSecret(loginResponse.mfaEnrollInfo?.secret)
        setIs2FAModalOpen(true)
        setStep(0)
      } else if (loginResponse.requireOTP) {
        setIsOTPModalOpen(true)
      }

      if (!loginResponse.success && !loginResponse.access_token) {
        handleErrorMessage(loginResponse.message)
      }
      setIsLoading(false)
    } catch (error) {
      console.error('[LOGIN ERROR] --> ', error)
      setIsLoading(false)
      toaster.error(
        error?.message ? error?.message : error?.data?.message ? error?.data?.message : t('messages:login.error'),
      )
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handles the verification of the MFA OTP. It calls the verify MFA API
   * and on success, proceeds to activate MFA for the user.
   */
  const handleVerifyMFA = async () => {
    try {
      setIsLoading(true)
      const response = await verifyMFA({
        otp,
        userEmail,
        userPassword,
      }).unwrap()

      if (response.secret) {
        handleActivateMfaEnabled()
        setVerifySuccess(true)
        toaster.success(t('messages:login.otp.success'), { autoClose: 6000, closeButton: true })
      }

      setIsLoading(false)
    } catch (error) {
      console.error('[MFA VERIFY ERROR] --> ', error)
      toaster.error(error.message || t('messages:login.otp.incorrect'))
      setIsLoading(false)
    } finally {
      setOtp('')
      setQrCode('')
      setSecret('')
      setIsLoading(false)
    }
  }

  /**
   * After a successful login, this function decodes the JWT, fetches user services/permissions,
   * and dispatches actions to update the Redux store, effectively logging the user in.
   * @param {string} token - The JWT access token.
   */
  const setUserServices = async (token) => {
    const decoded = jwt_decode(token)
    const { emailClient, roles } = decoded
    const formattedRoles = roles.join(', ')
    ReactGA.set({ userId: `${decoded?.email}-${decoded?.emailClient}` })
    const clientKey = await getClientKeyWithAccessToken(token)

    const urlLogin = `${process.env.REACT_APP_BASEURL}/admin/roles/subservices?emailClient=${emailClient}&roles=${formattedRoles}`
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'client-key': clientKey,
      },
    }
    fetch(urlLogin, options)
      .then((res) => res.json())
      .then((res) => {
        if (!res.success && typeof res.data === 'string') {
          throw Error(res.data)
        } else if (!res.success && !Array.isArray(res)) {
          throw Error(res.message)
        } else if (res && res.message) {
          dispatch(performSetServices([]))
        } else {
          dispatch(performSetServices(res))
          handleUserLogin({ access_token: token })
          handleErrorMessage('')
        }
      })
      .catch((error) => {
        console.error('[LOGIN ERROR] --> ', error)
        handleErrorMessage(error.message)
      })
  }

  /**
   * Calls the API to activate MFA for the currently logged-in user.
   */
  const handleActivateMfaEnabled = async () => {
    try {
      await activateMFA(userEmail).unwrap()
    } catch (error) {
      console.error('[MFA ENROLL ERROR] --> ', error)
      toaster.error(error.message || t('messages:login.otp.incorrect'))
    }
  }

  /**
   * Toggles the application theme between 'light' and 'dark' and dispatches the change to the Redux store.
   */
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    return dispatch(updateTheme(newTheme))
  }

  /**
   * Toggles the visibility of the password in the password input field.
   */
  const handleClickShowPassword = () => setShowPassword(!showPassword)

  return (
    <Box className="login" sx={{ backgroundColor: theme === 'light' ? '#ecefed' : '#242524' }}>
      <CssBaseline />
      <Paper className="login-wrapper" variant="colorSecondary" elevation={0}>
        <Box>
          <img src={theme === 'light' ? GuenoLogo : GuenoLogoDark} alt="güeno-logo" style={{ height: 65 }} />
        </Box>

        {!isLoading ? (
          <>
            <Box className="login-box">
              <InputLabel>{t('common:email')}:</InputLabel>
              <TextField
                value={userEmail}
                onChange={(evt) => handleInputUserEmail(evt)}
                onKeyDown={(evt) => handleKeyPress(evt)}
                placeholder={t('common:enterUserEmail')}
                fullWidth
                required
              />
            </Box>

            <Box className="login-box">
              <InputLabel>{t('common:password')}:</InputLabel>
              <TextField
                type={showPassword ? 'text' : 'password'}
                value={userPassword}
                fullWidth
                onChange={handleInputUserPassword}
                onKeyDown={(evt) => handleKeyPress(evt)}
                placeholder={t('common:enterPassword')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {isLoading ? (
              <ProcessingSpinner message={t('common:processing')} />
            ) : (
              <Button disabled={!isSignUpBtnOK} onClick={handleSignInBtn} variant="contained">
                {t('common:signIn')}
              </Button>
            )}
          </>
        ) : (
          <ProcessingSpinner />
        )}

        <TwoFactorActivationModal
          open={is2FAModalOpen}
          onClose={() => {
            setQrCode('')
            setSecret('')
            setOtp('')
            setIs2FAModalOpen(false)
            setVerifySuccess(false)
          }}
          qrCode={qrCode}
          onVerify={handleVerifyMFA}
          otp={otp}
          setOtp={setOtp}
          step={step}
          setStep={setStep}
          secret={secret}
          verifySuccess={verifySuccess}
          isProcessing={isLoading}
        />

        <OTPModal
          open={isOTPModalOpen}
          onClose={() => {
            setOtp('')
            setIsOTPModalOpen(false)
          }}
          onVerify={handleSignInBtn}
          otp={otp}
          setOtp={setOtp}
        />
      </Paper>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: '.25rem',
          padding: '1rem',
        }}
      >
        <Box className="icon-global-medium">
          <NightsStayOutlinedIcon />
        </Box>
        <IOSSwitch
          onChange={() => toggleTheme()}
          checked={theme === 'light' ? false : true}
          variant="large"
          thememode={theme}
        ></IOSSwitch>
      </Box>
    </Box>
  )
}

export default Login
